// Template Hierarchy Editor Page
// Reuses the existing hierarchy editing UI but connects to template JSONB storage
// Matches the interface and behavior of businesshierarchyPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useHierarchyOperations } from '../../hooks/useHierarchyOperations';
import { HierarchyNodeActions } from '../../components/hierarchy/HierarchyNodeActions';
import toast from 'react-hot-toast';

interface HierarchyNode {
  id: string;
  name: string;
  level_type: string;
  level: number;
  parent_id?: string;
  status: string;
  children?: HierarchyNode[];
  expanded?: boolean;
}

const TemplateHierarchyEditorPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [draggedNode, setDraggedNode] = useState<HierarchyNode | null>(null);
  const [dragOverNode, setDragOverNode] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    level_type: 'BUSINESS_GROUP',
    parent_id: '',
    level: 1,
    status: 'ACTIVE'
  });
  const [templateInfo, setTemplateInfo] = useState<{ 
    name: string; 
    description: string; 
    hierarchyType: string;
    marketplaceVisibility: string;
    status: string;
  } | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateSubType, setTemplateSubType] = useState<'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC'>('BUSINESS');
  const [templateMarketplaceVisibility, setTemplateMarketplaceVisibility] = useState<'DRAFT' | 'PRIVATE' | 'PUBLIC'>('DRAFT');
  const [templateStatus, setTemplateStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [showTemplateInfoForm, setShowTemplateInfoForm] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const loadTemplateInfo = async () => {
    if (!templateId) return;
    try {
      const response = await api.get(`/hierarchy-template-management/templates/${templateId}`);
      if (response.data.success && response.data.data) {
        const template = response.data.data;
        const content = typeof template.content === 'string' ? JSON.parse(template.content) : template.content;
        const hierarchyType = template.sub_type || content?.hierarchy_type || 'BUSINESS';
        const marketplaceVisibility = template.marketplace_visibility || 'DRAFT';
        const status = template.status || 'ACTIVE';
        
        setTemplateInfo({
          name: template.name,
          description: template.description || '',
          hierarchyType: hierarchyType,
          marketplaceVisibility: marketplaceVisibility,
          status: status
        });
        setTemplateName(template.name);
        setTemplateDescription(template.description || '');
        setTemplateSubType(hierarchyType as 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC');
        setTemplateMarketplaceVisibility(marketplaceVisibility as 'DRAFT' | 'PRIVATE' | 'PUBLIC');
        setTemplateStatus(status as 'ACTIVE' | 'INACTIVE');
        setAccessDenied(false);
      }
    } catch (err: any) {
      console.error('Error loading template info:', err);
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setError(err.response?.data?.error || 'You do not have permission to view this template');
        setLoading(false);
      }
    }
  };

  const handleSaveTemplateInfo = async () => {
    if (!templateId) return;
    
    try {
      const response = await api.put(`/hierarchy-template-management/templates/${templateId}`, {
        name: templateName,
        description: templateDescription,
        hierarchy_type: templateSubType,
        is_public: templateMarketplaceVisibility === 'PUBLIC',
        status: templateStatus
      });
      
      if (response.data.success) {
        setTemplateInfo(prev => prev ? {
          ...prev,
          name: templateName,
          description: templateDescription,
          hierarchyType: templateSubType,
          marketplaceVisibility: templateMarketplaceVisibility,
          status: templateStatus
        } : null);
        setShowTemplateInfoForm(false);
        toast.success('Template information updated successfully');
      }
    } catch (err: any) {
      console.error('Error updating template info:', err);
      toast.error(err.response?.data?.error || 'Failed to update template information');
    }
  };

  const loadHierarchy = async (): Promise<void> => {
    if (!templateId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/hierarchy-template-management/template-hierarchy/${templateId}/nodes`);
      const data = response.data.data || response.data;
      const treeData = buildHierarchyTree(data);
      setHierarchy(treeData);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setError(err.response?.data?.error || 'You do not have permission to view this template');
      } else {
        setError('Failed to load template hierarchy');
      }
      console.error('Error loading template hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  const {
    selectedNode,
    setSelectedNode,
  } = useHierarchyOperations({
    hierarchyType: 'business',
    onRefresh: loadHierarchy,
    onError: setError,
  });

  useEffect(() => {
    if (templateId) {
      loadTemplateInfo();
      loadHierarchy();
    }
  }, [templateId]);

  const buildHierarchyTree = (data: any[]): HierarchyNode[] => {
    const map = new Map();
    const roots: HierarchyNode[] = [];

    data.forEach(item => {
      map.set(item.id, { ...item, children: [], expanded: false });
    });

    data.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id && map.has(item.parent_id)) {
        const parent = map.get(item.parent_id);
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const toggleNode = (nodeId: string, nodes: HierarchyNode[]): HierarchyNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, expanded: !node.expanded };
      }
      if (node.children) {
        return { ...node, children: toggleNode(nodeId, node.children) };
      }
      return node;
    });
  };

  const handleToggleNode = (nodeId: string) => {
    setHierarchy(prev => toggleNode(nodeId, prev));
  };

  const handleAddNode = () => {
    // Auto-determine level type and level based on whether it's a root or child node
    let level_type = 'BUSINESS_GROUP'; // Default for root nodes
    let level = 1; // Default for root nodes
    let parent_id = null;
    
    if (selectedNode) {
      // Adding child node - determine based on parent
      parent_id = selectedNode.id;
      level = selectedNode.level + 1;
      
      // Determine level type based on parent's level type
      switch (selectedNode.level_type) {
        case 'WORLDWIDE':
          level_type = 'BUSINESS_GROUP';
          break;
        case 'BUSINESS_GROUP':
          level_type = 'BUSINESS_ENTITY';
          break;
        case 'BUSINESS_ENTITY':
          level_type = 'BUSINESS_LOCATION';
          break;
        default:
          level_type = 'BUSINESS_LOCATION';
      }
    }
    
    setFormData({
      name: '',
      level_type: level_type,
      parent_id: parent_id || '',
      level: level,
      status: 'ACTIVE'
    });
    setShowAddForm(true);
    setShowEditForm(false);
  };

  const handleEditNode = (node: HierarchyNode) => {
    setSelectedNode(node);
    setFormData({
      name: node.name,
      level_type: node.level_type,
      parent_id: node.parent_id || '',
      level: node.level,
      status: node.status
    });
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateId) return;

    try {
      if (showAddForm) {
        const response = await api.post(`/hierarchy-template-management/template-hierarchy/${templateId}/nodes`, {
          name: formData.name,
          level_type: formData.level_type,
          parent_id: formData.parent_id || null,
          level: formData.level,
          status: formData.status
        });
        
        if (response.data.success) {
          // Reload hierarchy to show the new node immediately
          await loadHierarchy();
          
          // If we added a child, expand the parent to show it
          if (formData.parent_id) {
            setTimeout(() => {
              setHierarchy(prev => {
                const expandParent = (nodes: HierarchyNode[]): HierarchyNode[] => {
                  return nodes.map(node => {
                    if (node.id === formData.parent_id) {
                      return { ...node, expanded: true };
                    }
                    if (node.children) {
                      return { ...node, children: expandParent(node.children) };
                    }
                    return node;
                  });
                };
                return expandParent(prev);
              });
            }, 100);
          }
          
          setShowAddForm(false);
          setSelectedNode(null);
          toast.success('Node added successfully');
        }
      } else if (showEditForm && selectedNode) {
        const parentChanged = selectedNode.parent_id !== formData.parent_id;
        
        const updateResponse = await api.put(`/hierarchy-template-management/template-hierarchy/${templateId}/nodes/${selectedNode.id}`, {
          name: formData.name,
          status: formData.status
        });

        if (updateResponse.data.success) {
          if (parentChanged) {
            const parentResponse = await api.put(`/hierarchy-template-management/template-hierarchy/${templateId}/nodes/${selectedNode.id}/parent`, {
              newParentId: formData.parent_id || null
            });
            
            if (!parentResponse.data.success) {
              setError('Failed to update parent node');
              return;
            }
          }
          
          await loadHierarchy();
          setShowEditForm(false);
          setSelectedNode(null);
          toast.success('Node updated successfully');
        }
      }
      
      setFormData({ name: '', level_type: 'BUSINESS_GROUP', parent_id: '', level: 1, status: 'ACTIVE' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save changes');
      console.error('Error saving node:', err);
      toast.error('Failed to save changes');
    }
  };

  const handleDeleteNode = async (node: HierarchyNode) => {
    if (!templateId) return;
    
    try {
      const response = await api.delete(`/hierarchy-template-management/template-hierarchy/${templateId}/nodes/${node.id}`);
      
      if (response.status === 204 || response.data.success) {
        await loadHierarchy();
        setSelectedNode(null);
        toast.success('Node deleted successfully');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete node');
      console.error('Delete error:', err);
      toast.error('Failed to delete node');
    }
  };

  const handleDragStart = (e: React.DragEvent, node: HierarchyNode) => {
    setDraggedNode(node);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, node: HierarchyNode) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedNode && draggedNode.id !== node.id) {
      setDragOverNode(node.id);
      
      if (!node.expanded && hoverTimeout === null) {
        const timeout = setTimeout(() => {
          setHierarchy(prev => toggleNode(node.id, prev));
        }, 1000);
        setHoverTimeout(timeout);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverNode(null);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent, targetNode: HierarchyNode) => {
    e.preventDefault();
    
    if (!draggedNode || !templateId || draggedNode.id === targetNode.id) {
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    if (isDescendant(draggedNode, targetNode)) {
      setError('Cannot move node to its own descendant');
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    try {
      const response = await api.put(`/hierarchy-template-management/template-hierarchy/${templateId}/nodes/${draggedNode.id}/parent`, {
        newParentId: targetNode.id
      });
      
      if (response.data.success) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          setHoverTimeout(null);
        }
        
        await loadHierarchy();
        
        setTimeout(() => {
          setHierarchy(prev => {
            const expandTarget = (nodes: HierarchyNode[]): HierarchyNode[] => {
              return nodes.map(node => {
                if (node.id === targetNode.id) {
                  return { ...node, expanded: true };
                }
                if (node.children) {
                  return { ...node, children: expandTarget(node.children) };
                }
                return node;
              });
            };
            return expandTarget(prev);
          });
        }, 100);
        
        setSelectedNode(null);
        toast.success('Node moved successfully');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to move node');
      console.error('Error moving node:', err);
      toast.error('Failed to move node');
    }
    
    setDraggedNode(null);
    setDragOverNode(null);
  };

  const handleDragEnd = () => {
    setDraggedNode(null);
    setDragOverNode(null);
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const isDescendant = (ancestor: HierarchyNode, potentialDescendant: HierarchyNode): boolean => {
    if (!ancestor.children) return false;
    
    for (const child of ancestor.children) {
      if (child.id === potentialDescendant.id) return true;
      if (isDescendant(child, potentialDescendant)) return true;
    }
    return false;
  };

  const getAvailableParents = (currentNodeId?: string): HierarchyNode[] => {
    const flattenNodes = (nodes: HierarchyNode[]): HierarchyNode[] => {
      const result: HierarchyNode[] = [];
      nodes.forEach(node => {
        result.push(node);
        if (node.children) {
          result.push(...flattenNodes(node.children));
        }
      });
      return result;
    };

    const allNodes = flattenNodes(hierarchy);
    
    if (!currentNodeId) {
      return allNodes;
    }

    const currentNode = allNodes.find(node => node.id === currentNodeId);
    if (!currentNode) {
      return allNodes;
    }

    return allNodes.filter(node => {
      if (node.id === currentNodeId) return false;
      return !isDescendant(currentNode, node);
    });
  };

  const renderHierarchyNode = (node: HierarchyNode, level: number = 0) => (
    <div key={node.id} className="ml-4">
      <div 
        className={`flex items-center space-x-2 py-2 px-3 rounded-lg hover:bg-[var(--color-secondary-bg)] transition-colors ${
          selectedNode?.id === node.id ? 'bg-[var(--color-info)]/10 border border-[var(--color-info)]/20' : ''
        } ${
          dragOverNode === node.id ? 'bg-[var(--color-success)]/10 border-2 border-[var(--color-success)]/30' : ''
        } ${
          draggedNode?.id === node.id ? 'opacity-50' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, node)}
        onDragOver={(e) => handleDragOver(e, node)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, node)}
        onDragEnd={handleDragEnd}
      >
        <button
          onClick={() => handleToggleNode(node.id)}
          className="p-1 hover:bg-[var(--color-border)] rounded"
        >
          {node.children && node.children.length > 0 ? (
            node.expanded ? (
              <ChevronDownIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>
        
        <div className={`w-2 h-2 rounded-full ${
          node.status === 'ACTIVE' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-border)]'
        }`} />
        
        <span 
          className="flex-1 text-sm font-medium text-[var(--color-surface-text)] cursor-pointer hover:text-[var(--color-info)]"
          onClick={() => setSelectedNode(node)}
        >
          {node.name}
        </span>
        
        <span className="text-xs text-[var(--color-secondary-text)] bg-[var(--color-secondary-bg)] px-2 py-1 rounded">
          {node.level_type}
        </span>
        
        <HierarchyNodeActions
          node={node}
          onEdit={handleEditNode}
          onDelete={handleDeleteNode}
          onCopy={() => {
            toast('Copy functionality coming soon');
          }}
        />
      </div>
      
      {node.expanded && node.children && node.children.length > 0 && (
        <div className="ml-4 border-l-2 border-[var(--color-border)]">
          {node.children.map(child => renderHierarchyNode(child, level + 1))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[var(--color-surface-text)]">Loading template hierarchy...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <p className="text-[var(--color-error)] mb-4">{error}</p>
          {accessDenied && (
            <button
              type="button"
              onClick={() => navigate('/templates/hierarchy')}
              className="px-4 py-2 bg-[var(--color-info)] text-white rounded-md hover:opacity-90"
            >
              Back to Hierarchy Templates
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)] space-y-6 p-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
              Edit Hierarchy Template
            </h1>
            {templateInfo && !showTemplateInfoForm && (
              <div className="mt-2">
                <p className="text-[var(--color-secondary-text)] font-medium">{templateInfo.name}</p>
                {templateInfo.description && (
                  <p className="text-[var(--color-secondary-text)] text-xs opacity-75 mt-1">{templateInfo.description}</p>
                )}
                <button
                  onClick={() => setShowTemplateInfoForm(true)}
                  className="mt-2 text-xs text-[var(--color-info)] hover:text-[var(--color-info)]/90 underline"
                >
                  Edit Template Information
                </button>
              </div>
            )}
            {showTemplateInfoForm && (
              <div className="mt-4 bg-[var(--color-surface-bg)] rounded-lg p-4 border border-[var(--color-border)]">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                      placeholder="Enter template name"
                      required
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Description
                    </label>
                    <textarea
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                      placeholder="Enter template description"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Hierarchy Type *
                    </label>
                    <select
                      value={templateSubType}
                      onChange={(e) => setTemplateSubType(e.target.value as 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC')}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    >
                      <option value="BUSINESS">Business</option>
                      <option value="FUNCTIONAL">Functional</option>
                      <option value="GEOGRAPHIC">Geographic</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Marketplace Visibility *
                    </label>
                    <select
                      value={templateMarketplaceVisibility}
                      onChange={(e) => setTemplateMarketplaceVisibility(e.target.value as 'DRAFT' | 'PRIVATE' | 'PUBLIC')}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PRIVATE">Private</option>
                      <option value="PUBLIC">Public</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Status *
                    </label>
                    <select
                      value={templateStatus}
                      onChange={(e) => setTemplateStatus(e.target.value as 'ACTIVE' | 'INACTIVE')}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveTemplateInfo}
                      className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowTemplateInfoForm(false);
                        if (templateInfo) {
                          setTemplateName(templateInfo.name);
                          setTemplateDescription(templateInfo.description || '');
                          setTemplateSubType(templateInfo.hierarchyType as 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC');
                          setTemplateMarketplaceVisibility(templateInfo.marketplaceVisibility as 'DRAFT' | 'PRIVATE' | 'PUBLIC');
                          setTemplateStatus(templateInfo.status as 'ACTIVE' | 'INACTIVE');
                        }
                      }}
                      className="px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded hover:bg-[var(--color-border)]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate('/templates/manage')}
            className="px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded hover:bg-[var(--color-border)] ml-4"
          >
            Back to Templates
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hierarchy Tree */}
        <div className="lg:col-span-2 bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <BuildingOfficeIcon className="h-6 w-6 text-[var(--color-primary-text)]" />
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                Hierarchy Structure
              </h2>
            </div>
            <button
              onClick={handleAddNode}
              className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>
                {selectedNode 
                  ? `Add Child to "${selectedNode.name}"` 
                  : 'Add Root Node'
                }
              </span>
            </button>
          </div>
          
          <div className="mb-2 text-sm text-[var(--color-surface-text)] opacity-75">
            💡 Click on a node to select it, then click &quot;Add Child&quot; to add a child node
            <br />
            🖱️ Drag and drop nodes to reorganize the hierarchy
          </div>
          <div className="max-h-96 overflow-y-auto">
            {hierarchy.length === 0 ? (
              <div className="text-center py-8 text-[var(--color-secondary-text)]">
                No nodes in template. Click &quot;Add Root Node&quot; to get started.
              </div>
            ) : (
              hierarchy.map(node => renderHierarchyNode(node))
            )}
          </div>
        </div>

        {/* Actions Panel */}
        <div className="space-y-6">
          {/* Selected Node Info */}
          {selectedNode && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                Selected Node
              </h3>
              <div className="space-y-2">
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Name:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Type:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.level_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Level:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.level}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Status:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.status}</p>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Form */}
          {(showAddForm || showEditForm) && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                {showAddForm ? 'Add New Node' : 'Edit Node'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    placeholder="Enter node name"
                    required
                    autoFocus
                  />
                </div>

                {/* Level Type Field */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                    Type
                  </label>
                  <select
                    value={formData.level_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, level_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                  >
                    <option value="WORLDWIDE">Worldwide</option>
                    <option value="BUSINESS_GROUP">Business Group</option>
                    <option value="BUSINESS_ENTITY">Business Entity</option>
                    <option value="BUSINESS_LOCATION">Business Location</option>
                  </select>
                </div>

                {/* Parent Field - Only show in edit mode */}
                {showEditForm && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Parent Node
                    </label>
                    <select
                      value={formData.parent_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    >
                      <option value="">Root (No Parent)</option>
                      {getAvailableParents(selectedNode?.id).map(node => (
                        <option key={node.id} value={node.id}>
                          {node.name} ({node.level_type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Status Field */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
                  >
                    {showAddForm ? 'Add Node' : 'Update Node'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setShowEditForm(false);
                      setSelectedNode(null);
                      setFormData({ name: '', level_type: 'BUSINESS_GROUP', parent_id: '', level: 1, status: 'ACTIVE' });
                    }}
                    className="px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md hover:bg-[var(--color-border)] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateHierarchyEditorPage;
